import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  FieldSuggestion,
  PriorityMappingConfig,
  PriorityMappingMode,
  SingleFieldMappingEntry,
} from '../../../types/pipelineConfig';
import type {
  PriorityLevelDto,
  PriorityMatrixEntryDto,
  TenantPriorityConfigDto,
} from '../../../types';
import { FieldPathInput } from '../FieldPathInput';
import { apiClient } from '../../../services/api';

interface PriorityMappingSectionProps {
  config: PriorityMappingConfig;
  onChange: (config: PriorityMappingConfig) => void;
  fieldSuggestions?: FieldSuggestion[];
  tenantId?: string;
  disabled?: boolean;
  /** When true, renders as an optional override (for conditional profiles) */
  isOptional?: boolean;
}

const IMPACT_OPTIONS = ['High', 'Medium', 'Low'];
const URGENCY_OPTIONS = ['High', 'Medium', 'Low'];

function getNextMappingKey<TValue>(mappings: Record<string, TValue>): string {
  let key = '';
  let counter = 1;
  while (key === '' || key in mappings) {
    key = `value${counter}`;
    counter++;
  }
  return key;
}

function createRowId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function PriorityMappingSection({
  config,
  onChange,
  fieldSuggestions = [],
  tenantId,
  disabled = false,
  isOptional = false,
}: PriorityMappingSectionProps) {
  const [overrideEnabled, setOverrideEnabled] = useState(() => {
    if (!isOptional) return true;
    return (
      config.mode !== undefined &&
      (config.sourceField ||
        config.impactSourceField ||
        config.urgencySourceField ||
        (config.singleFieldMappings && Object.keys(config.singleFieldMappings).length > 0) ||
        (config.impactMappings && Object.keys(config.impactMappings).length > 0) ||
        (config.urgencyMappings && Object.keys(config.urgencyMappings).length > 0))
    );
  });

  const [priorityConfig, setPriorityConfig] = useState<TenantPriorityConfigDto | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchConfig = async () => {
      setLoadingConfig(true);
      try {
        let resolvedConfig: TenantPriorityConfigDto | null = null;

        if (tenantId) {
          try {
            resolvedConfig = await apiClient.getTenantPriorityConfig(tenantId);
          } catch {
            /* ignore — will try system default template */
          }
        }

        if (!resolvedConfig) {
          try {
            const templates = await apiClient.getSystemPriorityTemplates();
            const defaultTemplate = templates.find((template) => template.isDefault);
            if (defaultTemplate) {
              resolvedConfig = {
                id: defaultTemplate.id,
                tenantId: tenantId ?? '',
                priorityLevels: defaultTemplate.priorityLevels ?? [],
                impactLevels: defaultTemplate.impactLevels ?? [],
                urgencyLevels: defaultTemplate.urgencyLevels ?? [],
                priorityMatrix: defaultTemplate.priorityMatrix ?? [],
                templateName: defaultTemplate.name,
                isUsingSystemDefault: true,
                isActive: defaultTemplate.isActive ?? true,
              };
            }
          } catch {
            /* ignore — will show hardcoded fallback */
          }
        }

        if (!cancelled) setPriorityConfig(resolvedConfig);
      } catch {
        if (!cancelled) setPriorityConfig(null);
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleModeChange = useCallback(
    (mode: PriorityMappingMode) => {
      if (mode === 'singleField') {
        onChange({
          mode,
          sourceField: config.sourceField ?? '',
          singleFieldMappings: config.singleFieldMappings ?? {},
        });
      } else {
        onChange({
          mode,
          impactSourceField: config.impactSourceField ?? '',
          urgencySourceField: config.urgencySourceField ?? '',
          impactMappings: config.impactMappings ?? {},
          urgencyMappings: config.urgencyMappings ?? {},
        });
      }
    },
    [config, onChange],
  );

  const handleOverrideToggle = useCallback(
    (enabled: boolean) => {
      setOverrideEnabled(enabled);
      if (!enabled) {
        onChange({ mode: 'singleField' });
      }
    },
    [onChange],
  );

  // Impact/Urgency level names from tenant config (fallback to standard)
  const impactOptions = useMemo(() => {
    if (priorityConfig?.impactLevels?.length) {
      return priorityConfig.impactLevels
        .sort((a, b) => a.value - b.value)
        .map((l) => l.name);
    }
    return IMPACT_OPTIONS;
  }, [priorityConfig]);

  const urgencyOptions = useMemo(() => {
    if (priorityConfig?.urgencyLevels?.length) {
      return priorityConfig.urgencyLevels
        .sort((a, b) => a.value - b.value)
        .map((l) => l.name);
    }
    return URGENCY_OPTIONS;
  }, [priorityConfig]);

  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30">
        <div className="flex items-center gap-2">
          <span className="text-indigo-500">🎯</span>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Priority Mapping
          </h4>
          {!isOptional && (
            <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
              Required
            </span>
          )}
        </div>
        {isOptional && (
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={!!overrideEnabled}
              onChange={(e) => handleOverrideToggle(e.target.checked)}
              disabled={disabled}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Override default mapping
          </label>
        )}
      </div>

      {(!isOptional || overrideEnabled) && (
        <div className="p-3 space-y-4">
          {/* Mode selector */}
          <div className="flex gap-3">
            <label
              className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                config.mode === 'singleField'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                name="priorityMappingMode"
                value="singleField"
                checked={config.mode === 'singleField'}
                onChange={() => handleModeChange('singleField')}
                disabled={disabled}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Single source field
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  One field maps to both Impact &amp; Urgency
                </div>
              </div>
            </label>
            <label
              className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                config.mode === 'twoFields'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                name="priorityMappingMode"
                value="twoFields"
                checked={config.mode === 'twoFields'}
                onChange={() => handleModeChange('twoFields')}
                disabled={disabled}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Two source fields
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Separate fields for Impact &amp; Urgency
                </div>
              </div>
            </label>
          </div>

          {/* Mode-specific editors */}
          {config.mode === 'singleField' ? (
            <SingleFieldModeEditor
              config={config}
              onChange={onChange}
              fieldSuggestions={fieldSuggestions}
              impactOptions={impactOptions}
              urgencyOptions={urgencyOptions}
              disabled={disabled}
            />
          ) : (
            <TwoFieldsModeEditor
              config={config}
              onChange={onChange}
              fieldSuggestions={fieldSuggestions}
              impactOptions={impactOptions}
              urgencyOptions={urgencyOptions}
              disabled={disabled}
            />
          )}

          {/* Dynamic priority matrix info */}
          <DynamicPriorityMatrix config={priorityConfig} loading={loadingConfig} />
        </div>
      )}
    </div>
  );
}

// --- Single Field Mode Editor ---

interface SingleFieldModeEditorProps {
  config: PriorityMappingConfig;
  onChange: (config: PriorityMappingConfig) => void;
  fieldSuggestions: FieldSuggestion[];
  impactOptions: string[];
  urgencyOptions: string[];
  disabled: boolean;
}

function SingleFieldModeEditor({
  config,
  onChange,
  fieldSuggestions,
  impactOptions,
  urgencyOptions,
  disabled,
}: SingleFieldModeEditorProps) {
  const mappings = useMemo(() => config.singleFieldMappings ?? {}, [config.singleFieldMappings]);
  type SingleFieldRow = {
    id: string;
    sourceValue: string;
    draftSourceValue: string;
    entry: SingleFieldMappingEntry;
  };

  const [rows, setRows] = useState<SingleFieldRow[]>(() =>
    Object.entries(mappings).map(([sourceValue, entry]) => ({
      id: createRowId('single-field-row'),
      sourceValue,
      draftSourceValue: sourceValue,
      entry,
    })),
  );

  const syncedRows = useMemo(() => {
    const previousRowsBySourceValue = new Map(rows.map((row) => [row.sourceValue, row]));
    return Object.entries(mappings).map(([sourceValue, entry]) => {
      const existing = previousRowsBySourceValue.get(sourceValue);
      if (!existing) {
        return {
          id: createRowId('single-field-row'),
          sourceValue,
          draftSourceValue: sourceValue,
          entry,
        };
      }
      return {
        ...existing,
        sourceValue,
        entry,
      };
    });
  }, [mappings, rows]);

  const toMappings = useCallback((nextRows: SingleFieldRow[]) => {
    const nextMappings: Record<string, SingleFieldMappingEntry> = {};
    for (const row of nextRows) {
      nextMappings[row.sourceValue] = row.entry;
    }
    return nextMappings;
  }, []);

  const emitRows = useCallback(
    (nextRows: SingleFieldRow[]) => {
      onChange({
        ...config,
        singleFieldMappings: toMappings(nextRows),
      });
    },
    [config, onChange, toMappings],
  );

  const updateSourceField = useCallback(
    (value: string) => {
      onChange({ ...config, sourceField: value });
    },
    [config, onChange],
  );

  const addEntry = useCallback(() => {
    const key = getNextMappingKey(toMappings(syncedRows));
    const nextRows = [
      ...syncedRows,
      {
        id: createRowId('single-field-row'),
        sourceValue: key,
        draftSourceValue: key,
        entry: { impact: 'Medium', urgency: 'Medium' },
      },
    ];
    setRows(nextRows);
    emitRows(nextRows);
  }, [emitRows, syncedRows, toMappings]);

  const updateDraftSourceValue = useCallback((rowId: string, sourceValue: string) => {
    setRows((current) =>
      current.map((row) => (row.id === rowId ? { ...row, draftSourceValue: sourceValue } : row)),
    );
  }, []);

  const renameEntryOnBlur = useCallback(
    (rowId: string) => {
      const targetRow = syncedRows.find((row) => row.id === rowId);
      if (!targetRow) return;

      const nextSourceValue = targetRow.draftSourceValue;
      if (nextSourceValue === targetRow.sourceValue) return;

      const nextRows = syncedRows.map((row) =>
        row.id === rowId
          ? { ...row, sourceValue: nextSourceValue, draftSourceValue: nextSourceValue }
          : row,
      );
      setRows(nextRows);
      emitRows(nextRows);
    },
    [emitRows, syncedRows],
  );

  const updateEntryLevels = useCallback(
    (rowId: string, entry: SingleFieldMappingEntry) => {
      const nextRows = syncedRows.map((row) => (row.id === rowId ? { ...row, entry } : row));
      setRows(nextRows);
      emitRows(nextRows);
    },
    [emitRows, syncedRows],
  );

  const removeEntry = useCallback(
    (rowId: string) => {
      const nextRows = syncedRows.filter((row) => row.id !== rowId);
      setRows(nextRows);
      emitRows(nextRows);
    },
    [emitRows, syncedRows],
  );

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Source Field
        </label>
        <FieldPathInput
          value={config.sourceField ?? ''}
          onChange={updateSourceField}
          suggestions={fieldSuggestions}
          placeholder="e.g., data.essentials.severity"
          disabled={disabled}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Value Mappings
        </label>
        <div className="border rounded-lg dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                <th className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Source Value
                </th>
                <th className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Impact
                </th>
                <th className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Urgency
                </th>
                <th className="px-3 py-1.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {syncedRows.map((row) => {
                return (
                <tr
                  key={row.id}
                  className="border-t dark:border-gray-700"
                >
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={row.draftSourceValue}
                      onChange={(e) => updateDraftSourceValue(row.id, e.target.value)}
                      onBlur={() => renameEntryOnBlur(row.id)}
                      disabled={disabled}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.entry.impact}
                      onChange={(e) =>
                        updateEntryLevels(row.id, {
                          ...row.entry,
                          impact: e.target.value,
                        })
                      }
                      disabled={disabled}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {impactOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.entry.urgency}
                      onChange={(e) =>
                        updateEntryLevels(row.id, {
                          ...row.entry,
                          urgency: e.target.value,
                        })
                      }
                      disabled={disabled}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {urgencyOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeEntry(row.id)}
                      disabled={disabled}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      title="Remove mapping"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
                );
              })}
              {syncedRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-center text-xs text-gray-400">
                    No mappings defined. Add a mapping to map source values to Impact &amp; Urgency.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
            <button
              type="button"
              onClick={addEntry}
              disabled={disabled}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50"
            >
              + Add Mapping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Two Fields Mode Editor ---

interface TwoFieldsModeEditorProps {
  config: PriorityMappingConfig;
  onChange: (config: PriorityMappingConfig) => void;
  fieldSuggestions: FieldSuggestion[];
  impactOptions: string[];
  urgencyOptions: string[];
  disabled: boolean;
}

function TwoFieldsModeEditor({
  config,
  onChange,
  fieldSuggestions,
  impactOptions,
  urgencyOptions,
  disabled,
}: TwoFieldsModeEditorProps) {
  return (
    <div className="space-y-4">
      <SimpleMappingTable
        label="Impact"
        sourceField={config.impactSourceField ?? ''}
        onSourceFieldChange={(v) => onChange({ ...config, impactSourceField: v })}
        mappings={config.impactMappings ?? {}}
        onMappingsChange={(m) => onChange({ ...config, impactMappings: m })}
        targetOptions={impactOptions}
        fieldSuggestions={fieldSuggestions}
        disabled={disabled}
      />
      <SimpleMappingTable
        label="Urgency"
        sourceField={config.urgencySourceField ?? ''}
        onSourceFieldChange={(v) => onChange({ ...config, urgencySourceField: v })}
        mappings={config.urgencyMappings ?? {}}
        onMappingsChange={(m) => onChange({ ...config, urgencyMappings: m })}
        targetOptions={urgencyOptions}
        fieldSuggestions={fieldSuggestions}
        disabled={disabled}
      />
    </div>
  );
}

// --- Simple Mapping Table (for two-field mode) ---

interface SimpleMappingTableProps {
  label: string;
  sourceField: string;
  onSourceFieldChange: (value: string) => void;
  mappings: Record<string, string>;
  onMappingsChange: (mappings: Record<string, string>) => void;
  targetOptions: string[];
  fieldSuggestions: FieldSuggestion[];
  disabled: boolean;
}

function SimpleMappingTable({
  label,
  sourceField,
  onSourceFieldChange,
  mappings,
  onMappingsChange,
  targetOptions,
  fieldSuggestions,
  disabled,
}: SimpleMappingTableProps) {
  type SimpleMappingRow = {
    id: string;
    sourceValue: string;
    draftSourceValue: string;
    value: string;
  };

  const [rows, setRows] = useState<SimpleMappingRow[]>(() =>
    Object.entries(mappings).map(([sourceValue, value]) => ({
      id: createRowId(`${label.toLowerCase()}-mapping-row`),
      sourceValue,
      draftSourceValue: sourceValue,
      value,
    })),
  );

  const syncedRows = useMemo(() => {
    const previousRowsBySourceValue = new Map(rows.map((row) => [row.sourceValue, row]));
    return Object.entries(mappings).map(([sourceValue, value]) => {
      const existing = previousRowsBySourceValue.get(sourceValue);
      if (!existing) {
        return {
          id: createRowId(`${label.toLowerCase()}-mapping-row`),
          sourceValue,
          draftSourceValue: sourceValue,
          value,
        };
      }
      return {
        ...existing,
        sourceValue,
        value,
      };
    });
  }, [label, mappings, rows]);

  const toMappings = useCallback((nextRows: SimpleMappingRow[]) => {
    const nextMappings: Record<string, string> = {};
    for (const row of nextRows) {
      nextMappings[row.sourceValue] = row.value;
    }
    return nextMappings;
  }, []);

  const addEntry = useCallback(() => {
    const key = getNextMappingKey(toMappings(syncedRows));
    const nextRows = [
      ...syncedRows,
      {
        id: createRowId(`${label.toLowerCase()}-mapping-row`),
        sourceValue: key,
        draftSourceValue: key,
        value: targetOptions[1] ?? targetOptions[0] ?? '',
      },
    ];
    setRows(nextRows);
    onMappingsChange(toMappings(nextRows));
  }, [label, onMappingsChange, syncedRows, targetOptions, toMappings]);

  const updateDraftSourceValue = useCallback((rowId: string, sourceValue: string) => {
    setRows((current) =>
      current.map((row) => (row.id === rowId ? { ...row, draftSourceValue: sourceValue } : row)),
    );
  }, []);

  const renameEntryOnBlur = useCallback(
    (rowId: string) => {
      const targetRow = syncedRows.find((row) => row.id === rowId);
      if (!targetRow) return;

      const nextSourceValue = targetRow.draftSourceValue;
      if (nextSourceValue === targetRow.sourceValue) return;

      const nextRows = syncedRows.map((row) =>
        row.id === rowId
          ? { ...row, sourceValue: nextSourceValue, draftSourceValue: nextSourceValue }
          : row,
      );
      setRows(nextRows);
      onMappingsChange(toMappings(nextRows));
    },
    [onMappingsChange, syncedRows, toMappings],
  );

  const updateEntryValue = useCallback(
    (rowId: string, value: string) => {
      const nextRows = syncedRows.map((row) => (row.id === rowId ? { ...row, value } : row));
      setRows(nextRows);
      onMappingsChange(toMappings(nextRows));
    },
    [onMappingsChange, syncedRows, toMappings],
  );

  const removeEntry = useCallback(
    (rowId: string) => {
      const nextRows = syncedRows.filter((row) => row.id !== rowId);
      setRows(nextRows);
      onMappingsChange(toMappings(nextRows));
    },
    [onMappingsChange, syncedRows, toMappings],
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          {label} Mapping
        </span>
      </div>
      <div className="p-3 space-y-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Source Field
          </label>
          <FieldPathInput
            value={sourceField}
            onChange={onSourceFieldChange}
            suggestions={fieldSuggestions}
            placeholder={`e.g., data.essentials.${label.toLowerCase()}`}
            disabled={disabled}
          />
        </div>
        <div className="border rounded dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                <th className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Source Value
                </th>
                <th className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {label}
                </th>
                <th className="px-3 py-1.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {syncedRows.map((row) => {
                return (
                <tr key={row.id} className="border-t dark:border-gray-700">
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={row.draftSourceValue}
                      onChange={(e) => updateDraftSourceValue(row.id, e.target.value)}
                      onBlur={() => renameEntryOnBlur(row.id)}
                      disabled={disabled}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.value}
                      onChange={(e) => updateEntryValue(row.id, e.target.value)}
                      disabled={disabled}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {targetOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeEntry(row.id)}
                      disabled={disabled}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      title="Remove mapping"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
                );
              })}
              {syncedRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-3 text-center text-xs text-gray-400">
                    No mappings defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
            <button
              type="button"
              onClick={addEntry}
              disabled={disabled}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50"
            >
              + Add Mapping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Dynamic Priority Matrix ---

interface DynamicPriorityMatrixProps {
  config: TenantPriorityConfigDto | null;
  loading: boolean;
}

const FALLBACK_PRIORITY_LEVELS: PriorityLevelDto[] = [
  { value: 1, name: 'P1', color: '#DC2626' },
  { value: 2, name: 'P2', color: '#EA580C' },
  { value: 3, name: 'P3', color: '#CA8A04' },
  { value: 4, name: 'P4', color: '#16A34A' },
];

const FALLBACK_IMPACT_LEVELS: PriorityLevelDto[] = [
  { value: 1, name: 'High', color: '#DC2626' },
  { value: 2, name: 'Medium', color: '#CA8A04' },
  { value: 3, name: 'Low', color: '#16A34A' },
];

const FALLBACK_URGENCY_LEVELS: PriorityLevelDto[] = [
  { value: 1, name: 'High', color: '#DC2626' },
  { value: 2, name: 'Medium', color: '#CA8A04' },
  { value: 3, name: 'Low', color: '#16A34A' },
];

const FALLBACK_MATRIX: PriorityMatrixEntryDto[] = [
  { impactLevel: 1, urgencyLevel: 1, priorityLevel: 1 },
  { impactLevel: 1, urgencyLevel: 2, priorityLevel: 2 },
  { impactLevel: 1, urgencyLevel: 3, priorityLevel: 3 },
  { impactLevel: 2, urgencyLevel: 1, priorityLevel: 2 },
  { impactLevel: 2, urgencyLevel: 2, priorityLevel: 3 },
  { impactLevel: 2, urgencyLevel: 3, priorityLevel: 4 },
  { impactLevel: 3, urgencyLevel: 1, priorityLevel: 3 },
  { impactLevel: 3, urgencyLevel: 2, priorityLevel: 4 },
  { impactLevel: 3, urgencyLevel: 3, priorityLevel: 4 },
];

function DynamicPriorityMatrix({ config, loading }: DynamicPriorityMatrixProps) {
  const priorityLevels = config?.priorityLevels?.length
    ? [...config.priorityLevels].sort((a, b) => a.value - b.value)
    : FALLBACK_PRIORITY_LEVELS;
  const impactLevels = config?.impactLevels?.length
    ? [...config.impactLevels].sort((a, b) => a.value - b.value)
    : FALLBACK_IMPACT_LEVELS;
  const urgencyLevels = config?.urgencyLevels?.length
    ? [...config.urgencyLevels].sort((a, b) => a.value - b.value)
    : FALLBACK_URGENCY_LEVELS;
  const matrix = config?.priorityMatrix?.length ? config.priorityMatrix : FALLBACK_MATRIX;

  const priorityByValue = useMemo(() => {
    const map = new Map<number, PriorityLevelDto>();
    for (const p of priorityLevels) map.set(p.value, p);
    return map;
  }, [priorityLevels]);

  const lookupPriority = useCallback(
    (impactValue: number, urgencyValue: number): PriorityLevelDto | undefined => {
      const entry = matrix.find(
        (m) => m.impactLevel === impactValue && m.urgencyLevel === urgencyValue,
      );
      if (!entry || entry.priorityLevel === undefined) {
        return undefined;
      }
      return priorityByValue.get(entry.priorityLevel);
    },
    [matrix, priorityByValue],
  );

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 text-sm">
      <div className="flex items-start gap-2">
        <span className="text-blue-500">ℹ️</span>
        <div className="flex-1">
          <p className="font-medium text-blue-800 dark:text-blue-200">
            Priority is auto-calculated
          </p>
          <p className="mt-1 text-blue-700 dark:text-blue-300">
            Priority is automatically derived from Impact × Urgency using your configured matrix.
          </p>
          {loading ? (
            <div className="mt-2 text-xs text-blue-500">Loading priority matrix...</div>
          ) : (
            <>
              <div
                className="mt-2 grid gap-1 text-xs text-center"
                style={{
                  gridTemplateColumns: `auto repeat(${urgencyLevels.length}, 1fr)`,
                }}
              >
                {/* Header row: empty corner + urgency labels */}
                <div />
                {urgencyLevels.map((u) => (
                  <div key={u.value} className="font-semibold text-blue-800 dark:text-blue-200">
                    {u.name}
                  </div>
                ))}

                {/* Data rows: impact label + priority cells */}
                {impactLevels.map((imp) => (
                  <>
                    <div
                      key={`label-${imp.value}`}
                      className="font-semibold text-left text-blue-800 dark:text-blue-200 pr-2"
                    >
                      {imp.name}
                    </div>
                    {urgencyLevels.map((urg) => {
                      const priority = lookupPriority(imp.value, urg.value);
                      return (
                        <div
                          key={`${imp.value}-${urg.value}`}
                          className="rounded px-1 py-0.5"
                          style={{
                            backgroundColor: priority
                              ? `${priority.color}20`
                              : undefined,
                            color: priority?.color,
                          }}
                        >
                          {priority?.name ?? '—'}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
              <p className="mt-1 text-blue-600 dark:text-blue-400 text-xs">
                Rows = Impact, Columns = Urgency
                {config?.templateName && (
                  <span className="ml-2 text-xs text-blue-500">
                    {config.isUsingSystemDefault ? 'System Default: ' : 'Template: '}
                    {config.templateName}
                  </span>
                )}
              </p>
              <a
                href="/admin/itil-configuration"
                className="mt-1 inline-block text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Edit priority matrix →
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// getDefaultPriorityMappingConfig is intentionally not exported here
// to satisfy react-refresh/only-export-components. Use the inline
// default in NormalizationProfileEditor instead.
